import { lazy, Suspense, useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
  ANALYZING_CONTENT,
  NO_CONTENT_RECOMMENDATIONS,
} from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import LinearLoaderWithTime from "../Loaders/LinearLoaderWithTime";
import ModalCreateWithIA from "../Modal/ModalCreateWithIA";
import CarouselDisplay from "./CarouselDisplay";
import MediaUrlItem from "./MediaUrlItem";
import ContentRecommendationsBanner from "./ContentRecommendationsBanner";
import { useInternalMediaUrls } from "../../hooks/useInternalMediaUrls";
import { useContentRecommendations } from "../../hooks/useContentRecommendations";
import { isCarouselItem } from "../../utils/mediaUrlsAnalysis";

const ModalContentRecommendations = lazy(
  () => import("../Modal/ModalContentRecommendations")
);

const MediaUrlsAnalysisContainer = ({
  mediaUrls,
  onRemoveItem: onRemoveItemCallback,
  objective,
  platform,
  onChangeItem = () => {},
  campaignContext,
  disableAnalysis = false,
  itemSize = 160,
  suggestedCreativesCount = null,
  onCallbackCampaignMediaImport,
  containerRef,
}) => {
  const { t } = useTranslationApp();

  const { validMediaUrls, onChangeData, loaderProgress, setLoaderProgress } =
    useInternalMediaUrls(mediaUrls);

  const {
    contentRecommendations,
    completedContentRecommendationKeys,
    isNoContentRecommendations,
    hasAtLeastOneCreativeNotYoutube,
    isRecommendationsModalOpen,
    setIsRecommendationsModalOpen,
    createWithIAState,
    onSelectContentRecommendation,
    onCloseCreateWithIAModal,
    buildWrappedCampaignMediaImport,
    showAnalyzingContentLoader,
    showContentRecommendationsSummary,
  } = useContentRecommendations({
    mediaUrls,
    platform,
    objective,
    campaignContext,
    disableAnalysis,
    validMediaUrls,
  });

  const onWrappedCallbackCampaignMediaImport = useMemo(
    () => buildWrappedCampaignMediaImport(onCallbackCampaignMediaImport),
    [buildWrappedCampaignMediaImport, onCallbackCampaignMediaImport]
  );

  const onRemoveItem = (urlOrCarouselItem) => {
    if (
      typeof urlOrCarouselItem === "object" &&
      urlOrCarouselItem?.isCarousel
    ) {
      const originalIndex = mediaUrls.findIndex(
        (item) =>
          item?.isCarousel === true &&
          item?.carouselImages &&
          Array.isArray(item.carouselImages)
      );
      if (originalIndex !== -1) {
        onRemoveItemCallback(originalIndex);
      }
      return;
    }

    const originalIndex = mediaUrls.findIndex(
      (item) => item?.url === urlOrCarouselItem
    );
    if (originalIndex !== -1) {
      onRemoveItemCallback(originalIndex);
    }
  };

  if (!hasAtLeastOneCreativeNotYoutube) {
    return null;
  }

  return (
    <>
      <Box
        ref={containerRef}
        sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 1,
            overflowX: "auto",
            width: "100%",
          }}
        >
          {validMediaUrls.length > 0 &&
            validMediaUrls.map((mediaUrlData, index) => {
              if (isCarouselItem(mediaUrlData)) {
                const carouselUrls = mediaUrlData.carouselImages.map((img) =>
                  typeof img === "string" ? img : img?.url || img
                );
                const carouselMediaData = mediaUrlData.carouselImages.map(
                  (img) => (typeof img === "object" ? img.mediaData || {} : {})
                );

                return (
                  <CarouselDisplay
                    key={`carousel_${index}_${carouselUrls[0] || index}`}
                    urls={carouselUrls}
                    mediaData={carouselMediaData}
                    onRemove={() => onRemoveItem(mediaUrlData)}
                    itemSize={itemSize}
                  />
                );
              }

              return (
                <MediaUrlItem
                  key={mediaUrlData.url || `item_${index}`}
                  mediaUrlData={mediaUrlData}
                  onRemove={() => onRemoveItem(mediaUrlData?.url)}
                  onChangeData={onChangeData}
                  onAnalysisFinished={(analysis) => {
                    onChangeItem({
                      url: mediaUrlData.url,
                      data: { analysis },
                    });
                  }}
                  disableAnalysis={disableAnalysis}
                  itemSize={itemSize}
                />
              );
            })}
        </Box>

        {showAnalyzingContentLoader && (
          <LinearLoaderWithTime
            label={t(ANALYZING_CONTENT)}
            sxLabel={{
              fontWeight: 600,
              fontSize: "15px",
              textAlign: "left",
            }}
            time={30000}
            sx={{
              mt: 1.2,
              width: "100%",
            }}
            progress={loaderProgress}
            autoIncrement={true}
            onProgressChange={setLoaderProgress}
          />
        )}

        {showContentRecommendationsSummary && (
          <ContentRecommendationsBanner
            contentRecommendations={contentRecommendations}
            validMediaUrlsCount={validMediaUrls.length}
            suggestedCreativesCount={suggestedCreativesCount}
            onSeeMore={() => setIsRecommendationsModalOpen(true)}
          />
        )}

        {!showAnalyzingContentLoader &&
          !showContentRecommendationsSummary &&
          isNoContentRecommendations &&
          !disableAnalysis && (
            <Box>
              <Typography variant="body1">
                {t(NO_CONTENT_RECOMMENDATIONS)}
              </Typography>
            </Box>
          )}
      </Box>

      {isRecommendationsModalOpen && (
        <Suspense fallback={null}>
          <ModalContentRecommendations
            modalOpen={isRecommendationsModalOpen}
            onCloseModal={() => setIsRecommendationsModalOpen(false)}
            contentRecommendations={contentRecommendations}
            completedContentRecommendationKeys={
              completedContentRecommendationKeys
            }
            onSelectRecommendation={onSelectContentRecommendation}
          />
        </Suspense>
      )}

      {createWithIAState && (
        <ModalCreateWithIA
          key={createWithIAState.key}
          modalOpen={true}
          onCloseModal={onCloseCreateWithIAModal}
          initialValues={createWithIAState.initialValues}
          doReUpdateInitialValues={true}
          onCallbackCampaignMediaImport={onWrappedCallbackCampaignMediaImport}
        />
      )}
    </>
  );
};

export default MediaUrlsAnalysisContainer;
